/* ================================================================
   Ähnliche Objekte erkennen.

   Zwei Aufgaben:

   1. Beim Nutzer: Bevor ein neu angelegtes Objekt in den Katalog
      vorgeschlagen wird, soll er sehen, ob dort schon etwas
      Gleichartiges liegt — und bewusst entscheiden, ob er
      zusammenführt oder getrennt lässt. Solange das offen ist, wird
      nichts vorgeschlagen.

   2. Beim Admin: Beim Prüfen eines Aufnahmevorschlags sollen die
      ähnlichen Katalogeinträge direkt daneben stehen.

   Die Regel steht in `aehnlichkeit.js` (Projektwurzel) und gilt für
   Server und Client gleich: Name wortweise, Prädikat und Lage müssen
   passen, Winzer, Typ, Land und Rebsorte schließen aus, wenn beide
   Seiten sie kennen. Sie entscheidet nichts, sie fragt nur nach.
   ================================================================ */

import aehnlichkeit from "../../aehnlichkeit.js";

const { baueIndex, kompakt, merkmale, quelleVon, suche } = aehnlichkeit;

const LISTE = { wein: "weine", winzer: "winzer", region: "regions" };

/* Namensschlüssel — für Altaufrufer; die Regel selbst steht in
   aehnlichkeit.js (gemeinsam mit dem Server). */
const schluessel = (s) => kompakt(s);

/* Index je Typ über den Katalog — einmal je Katalogstand. */
let letzterKatalog = null;
let letzterIndex = null;
function katalogIndex(katalog) {
  if (katalog !== letzterKatalog) {
    letzterKatalog = katalog;
    letzterIndex = { region: baueIndex("region", katalog), winzer: baueIndex("winzer", katalog), wein: baueIndex("wein", katalog) };
  }
  return letzterIndex;
}

/** Katalogkandidaten zu einem eigenen Objekt — beste zuerst, je
    { objekt, stufe: "sehr"|"moeglich", merkmale }. */
function kandidaten(katalog, typ, objekt, db = null) {
  if (!katalog || !objekt) return [];
  return suche(katalogIndex(katalog)[typ], merkmale(typ, objekt, quelleVon(db || {})), { ausserId: objekt.id });
}

/* ----------------------------------------------------------------
   Offene Entscheidungen im eigenen Kellerbuch.

   Betrachtet werden nur Objekte, die noch nie im Katalog waren
   (keine Herkunft), nicht abgekoppelt sind und für die noch keine
   Entscheidung vorliegt (`getrennt`).

   Jeder Eintrag trägt `sperrt`: Nur eine SEHR wahrscheinliche
   Dublette (gleicher Name, gleicher Winzer bzw. Ortsbezug) hält den
   Vorschlag zurück. Eine bloß mögliche wird gezeigt, sperrt aber
   nicht — früher sperrte jeder Namenstreffer, und praktisch jeder
   neue „Riesling trocken“ blieb liegen.
   ---------------------------------------------------------------- */
function offeneEntscheidungen(db, katalog) {
  if (!db || !katalog) return [];
  const index = katalogIndex(katalog);
  const ns = quelleVon(db);
  const offen = [];
  for (const typ of ["region", "winzer", "wein"]) {
    for (const o of db[LISTE[typ]] || []) {
      if (o.herkunft || o.abgekoppelt || o.getrennt || o.nurKatalog) continue;
      const treffer = suche(index[typ], merkmale(typ, o, ns), { ausserId: o.id, grenze: 3 });
      if (treffer.length === 0) continue;
      offen.push({ typ, objekt: o, kandidaten: treffer, sperrt: treffer.some((k) => k.stufe === "sehr") });
    }
  }
  /* Sperrende zuerst. */
  return offen.sort((a, b) => Number(b.sperrt) - Number(a.sperrt));
}

/* IDs, für die eine Entscheidung aussteht — der Abgleich lässt sie
   aus, damit nichts vorgeschlagen wird, was wahrscheinlich eine
   Dublette ist. */
function gesperrteIds(db, katalog) {
  return new Set(offeneEntscheidungen(db, katalog).filter((e) => e.sperrt).map((e) => e.objekt.id));
}

/* „Getrennt lassen" — ab jetzt normal vorschlagen. */
function markiereGetrennt(db, typ, id) {
  const feld = LISTE[typ];
  if (!feld) return db;
  return {
    ...db,
    [feld]: (db[feld] || []).map((o) => o.id === id ? { ...o, getrennt: true } : o),
  };
}

export { gesperrteIds, kandidaten, markiereGetrennt, offeneEntscheidungen, schluessel };
